import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import Breadcrumbs from '../marketing/components/Breadcrumbs';
import ArticleCard from '../marketing/components/ArticleCard';
import { getCategory, articlesByCategory } from '../marketing/data';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

const BlogCategoryPage: React.FC = () => {
  useMarketingFonts();
  const { category } = useParams<{ category: string }>();
  const cat = getCategory(category);

  useSeoHead({
    title: cat ? `${cat.name} Articles for UK Landlords | MillOS Blog` : 'MillOS Blog',
    description: cat?.description || '',
    ogType: 'website',
    canonicalPath: cat ? `/blog/category/${cat.slug}` : undefined,
    jsonLd: cat
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: cat.name,
            description: cat.description,
            url: `https://www.millos.uk/blog/category/${cat.slug}`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.millos.uk/' },
                { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.millos.uk/blog' },
                { '@type': 'ListItem', position: 3, name: cat.name, item: `https://www.millos.uk/blog/category/${cat.slug}` },
              ],
            },
          },
        ]
      : [],
  });

  if (!cat) return <Navigate to="/blog" replace />;

  const articles = articlesByCategory(cat.slug);

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />
      <Breadcrumbs maxWidth={1000} items={[{ label: 'Home', to: '/' }, { label: 'Blog', to: '/blog' }, { label: cat.name }]} />
      <Box component="section" sx={{ padding: { xs: '20px 24px 8px', md: '20px 52px 8px' } }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: C.tintBlue, color: C.accent, borderRadius: '32px', padding: '6px 14px', fontSize: 12, fontWeight: 500, letterSpacing: '0.075rem', textTransform: 'uppercase' }}>
            Category
          </Box>
          <Box component="h1" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(2.5rem,5vw,4.25rem)', lineHeight: 1.1, letterSpacing: '-0.04em', marginTop: '20px' }}>
            {cat.name}
          </Box>
          <Box sx={{ maxWidth: '38rem', margin: '18px auto 0', fontSize: 17, color: C.body }}>{cat.description}</Box>
        </Box>
      </Box>
      <Box component="section" sx={{ padding: { xs: '32px 16px 64px', md: '48px 52px 96px' } }}>
        {articles.length > 0 ? (
          <Box sx={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3,1fr)' }, gap: '28px' }}>
            {articles.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </Box>
        ) : (
          <Box sx={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center', color: C.muted, padding: '40px 0' }}>
            No articles in this category yet.
          </Box>
        )}
      </Box>
      <MarketingFooter />
    </Box>
  );
};

export default BlogCategoryPage;
